import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UIRouter } from '@uirouter/angular';
import { ReferralsService } from '../referrals.service';

import { HelpTooltipDirective } from '../../../UI-Lib/Help-Tooltip';
import { ReferralClientInfoComponent } from './Client-Info/clientInfo.component';
import { ReferralSiteDetailsComponent } from './Site-Details/siteDetails.component';
import { ReferralScopeOfAppealComponent } from './Scope-Of-Appeal/scopeOfAppeal.component';
import { ReferralValueAssessmentsComponent } from './Value-Assessments/valueAssessments.component';
import { AttachmentModalData, AttachmentBundlerContext } from 'src/app/Attachment/attachment.modal.model';
import { Attachment } from 'src/app/Attachment/attachment.model';
import { CommentModalData } from 'src/app/Comments/comments.service';
import { EntityAttachmentsComponent } from 'src/app/Attachment/Entity/entity-attachments.component';
import { EntityCommentsButtonComponent } from 'src/app/Comments/Entity-Comments-Button/entityCommentsButton.component';
import { BusyIndicatorService } from 'src/app/Busy-Indicator';
import { assign } from 'lodash';
import { ReferralOverviewComponent } from './Overview/referralOverview.component';
import { BreadCrumb, BreadCrumbsComponent } from 'src/app/UI-Lib/Bread-Crumb/breadCrumbs.component';
import { TaskService } from 'src/app/Task/task.service.upgrade';
import { LaunchTaskModalService } from 'src/app/Task/launchTaskModal.service';
import { EntityTypeIds } from 'src/app/constants.new';
import { ReferralInputWithRevertComponent } from './Input-With-Revert/referralInputWithRevert.component';
import { MessageModalService } from 'src/app/UI-Lib/Message-Box/messageModal.service';
import { ReferralPageService } from './referralPage.service';
import { ServiceProviderFeesComponent } from './Service-Provider-Fees/serviceProviderFees.component';
import { ReferralTaskExceptionModalComponent } from './Task-Exception-Modal/referralTaskExceptionModal.component';
import { WeissmanModalService } from '../../../Compliance/WeissmanModalService';
import { ToastrService } from 'ngx-toastr';
import { ActionViewPersistenceService } from 'src/app/Task/ActionView/Action.View.Persistence.Service.upgrade';
import { ActionViewStateService } from 'src/app/Task/ActionView/actionViewState.service';
import { UpgradeNavigationServiceHandler } from 'src/app/Common/Routing/upgrade-navigation-handler.service';
import { NavigationService } from 'src/app/Layout/navigation.service';
import { splitAndDedupeMessages } from '../../../Common/splitAndDedupeMessages';
import { ReferralRyanContactsComponent } from './Ryan-Contacts/ryanContacts.component';
import { AceLoaderComponent } from '../../../UI-Lib/ACE/Loader/loader.component';
import { ReferralRoutingComponent } from './Referral-Routing/referralRouting.component';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { RestrictService } from 'src/app/Common/Permissions/restrict.service';

declare const moment: any;

@Component({
    selector: 'referral-page',
    templateUrl: 'referralPage.component.html',
    styleUrls: ['referralPage.component.scss'],
    imports: [
        ReactiveFormsModule,
        HelpTooltipDirective,
        ReferralOverviewComponent,
        ReferralClientInfoComponent,
        ReferralSiteDetailsComponent,
        ReferralScopeOfAppealComponent,
        ReferralValueAssessmentsComponent,
        EntityAttachmentsComponent,
        EntityCommentsButtonComponent,
        BreadCrumbsComponent,
        ServiceProviderFeesComponent,
        ReferralInputWithRevertComponent,
        ReferralRyanContactsComponent,
        AceLoaderComponent,
        ReferralRoutingComponent,
        TooltipDirective,
    ],
    providers: [ReferralPageService],
})
export class ReferralPageComponent implements OnInit {
    constructor(
        private readonly _referralsService: ReferralsService,
        private readonly _router: UIRouter,
        private readonly _fb: FormBuilder,
        private readonly _busyIndicatorService: BusyIndicatorService,
        private readonly _taskService: TaskService,
        private readonly _launchTaskModalService: LaunchTaskModalService,
        private readonly _messageModalService: MessageModalService,
        private readonly _referralPageService: ReferralPageService,
        private readonly _modalService: WeissmanModalService,
        private readonly _toastr: ToastrService,
        private readonly _actionViewPersistenceService: ActionViewPersistenceService,
        private readonly actionViewStateService: ActionViewStateService,
        private readonly _navigationServiceHandler: UpgradeNavigationServiceHandler,
        private readonly _navigationService: NavigationService,
        private readonly _restrictService: RestrictService,
    ) {}

    static readonly ProcessReferralPrepareStateName = 'processReferralPrepare';
    static readonly ProcessReferralCompleteStateName = 'processReferralComplete';

    @ViewChild('siteDetails') siteDetails: ReferralSiteDetailsComponent;

    referralId: number;
    // Batch processing (preview-then-transfer-to-RLS from Action View): list of task IDs being processed in sequence
    taskList: number[];
    taskIndex: number = 0;
    isBatchMode: boolean = false;
    referral: Core.LitigationReferralModel;
    systemReferral: Core.LitigationReferralModel;
    originalReferral: Core.LitigationReferralModel;
    referralForm: FormGroup;
    attachmentModalData: AttachmentModalData;
    commentModalData: CommentModalData;
    hasAttachments: boolean;
    hasComments: boolean;
    enteringEditMode: boolean = false;
    breadcrumbs: BreadCrumb[] = [];
    coCounselFirmOptions: Weissman.Model.NameValuePair[] = [];

    rlsFeeTypeOptions: string[] = ['Fixed', 'Hourly', 'Hybrid', 'Included', 'N/A', 'Performance Based'];

    editMode = this._referralPageService.editMode;
    workflowStatus = this._referralPageService.workflowStatus;
    hasWritePermission: boolean = false;

    private _pristineReferral: Core.LitigationReferralModel;
    private _currentAttachments: Attachment[] = [];
    private _coCounselFirms: Core.CoCounselFirmModel[] = [];

    async ngOnInit(): Promise<void> {
        this._initForm();

        const stateName = this._router.globals.current?.name;
        if (
            stateName === ReferralPageComponent.ProcessReferralPrepareStateName ||
            stateName === ReferralPageComponent.ProcessReferralCompleteStateName
        ) {
            const timestamp = this._router.globals.params['timestamp'];
            const taskString: string = sessionStorage[`${stateName}${timestamp}`];
            this.taskList = taskString ? JSON.parse(taskString) : [];
            this.isBatchMode = this.taskList.length > 0;
        }

        if (this.isBatchMode) {
            await this._loadReferralForTask(this.taskList[this.taskIndex]);
        } else {
            this.referralId = +this._router.globals.params['referralId'];
            this._loadAllData();
        }
    }

    get canModifyReferral(): boolean {
        return this._referralPageService.workflowStatus()?.canModifyReferral ?? false;
    }

    get taskStatusName(): string {
        if (!this.workflowStatus()) {
            return '';
        }

        if (!this.workflowStatus().isSeriesCompleted && this.workflowStatus().taskRescindName) {
            return `${this.workflowStatus().taskRescindName}; ${this.workflowStatus().taskAdvanceName}`;
        }

        return this.workflowStatus().taskAdvanceName;
    }

    get taskAdvanceName(): string {
        switch (this.workflowStatus().taskTypeId) {
            case Core.TaskTypes.PrepareDraftLitigationReferral:
                return 'Complete Draft';
            case Core.TaskTypes.TransferLitigationReferralToRLS:
                return 'Transfer to RLS';
            default:
                return 'Complete';
        }
    }

    async edit(): Promise<void> {
        this.enteringEditMode = true;
        try {
            this.systemReferral = await this._referralsService.getLitigationReferralLiveData(this.referralId);
            this._pristineReferral = structuredClone(this.referral);
            this._turnEditModeOn();
        } finally {
            this.enteringEditMode = false;
        }
    }

    async cancelEdit(): Promise<void> {
        const busyRef = this._busyIndicatorService.show({ message: 'Loading' });
        try {
            this.originalReferral = await this._referralsService.getLitigationReferralOriginalData(this.referralId);
            this.referral = this._pristineReferral;
            this.referralForm.reset();
            this._patchForm();
            this._turnEditModeOff();
        } finally {
            busyRef.hide();
        }
    }

    async save(): Promise<void> {
        if (!this._validatePercent()) {
            return;
        }

        if (!this._validateForm()) {
            return;
        }

        const busyRef = this._busyIndicatorService.show({ message: 'Saving' });
        try {
            assign(this.referral, this.referralForm.value);

            if (
                !!this.referral.appealDeadlineDate &&
                this.referral.appealDeadlineDate !== this._pristineReferral.appealDeadlineDate &&
                !this.referral.rlsDueDate
            ) {
                // If the appeal deadline date has changed and the RLS due date is not set,
                // calculate a RLS due date based on the new appeal deadline date.
                const newRLSDueDate: Date = moment(this.referral.appealDeadlineDate).subtract(14, 'days').toDate();
                this.referral.rlsDueDate = newRLSDueDate;
                this.referralForm.patchValue({
                    rlsDueDate: newRLSDueDate,
                });
            }

            await this._referralsService.updateLitigationReferral(this.referral);
            this._setCoCounselFirmOptions();
            this._turnEditModeOff();
        } catch (e) {
            this.resetForm();

            throw e;
        } finally {
            busyRef.hide();
        }
    }

    resetForm(): void {
        this.referral = structuredClone(this._pristineReferral);
        this._patchForm();
    }

    async advanceTask() {
        if (this.workflowStatus().taskTypeId !== Core.TaskTypes.PrepareDraftLitigationReferral) {
            try {
                const message = `Are you sure you wish to ${this.workflowStatus()?.taskAdvanceName}?`;
                await this._messageModalService.confirm(message, 'Confirm Completion');
            } catch (e) {
                return Promise.resolve(false);
            }
        }

        const busyRef = this._busyIndicatorService.show({ message: 'Advancing Task' });

        const taskUpdateModel: Core.LitigationReferralTaskUpdateModel = {
            taskId: this.workflowStatus().taskID,
            litigationReferralId: this.referral.litigationReferralId,
        };

        let result = {} as Core.LitigationReferralTaskUpdateResultModel;

        try {
            result = await this._referralsService.completeTask(this.referral.litigationReferralId, taskUpdateModel);
            await this._setWorkflowStatus();

            if (this.workflowStatus().isSeriesCompleted) {
                this.referral = await this._referralsService.getLitigationReferral(this.referral.litigationReferralId);
                this._patchForm();
            }
        } finally {
            busyRef.hide();
        }

        if (this.workflowStatus().canModifyReferral && this.editMode()) {
            this.cancelEdit();
        }

        if (result.succeeded) {
            if (this.isBatchMode) {
                // In batch mode, the task is completed; move on to the next item or finish.
                await this._advanceBatch();
                return;
            }
            // Show any warning message (e.g., attachment transfer failures) instead of success message
            if (result.warningMessage) {
                await this._messageModalService.alert(result.warningMessage, 'Warning');
            } else if (this.workflowStatus().isTransferToRLSCompleted) {
                const message = 'You have successfully transferred this referral to RLS.';
                await this._messageModalService.alert(message, 'Referral Transferred');
            } else if (this.workflowStatus().isPrepareDraftCompleted) {
                const message = 'You have successfully completed the Prepare Litigation Referral Task.';
                await this._messageModalService.alert(message, 'Prepare Draft Completed');
            }
        } else if (result.errorMessage || result.warningMessage) {
            // In batch mode the task is not completed on failure; show error details and stay on the current referral.
            // Use shared splitAndDedupeMessages utility for better formatting
            this._taskService.showErrorNotificationModal(
                splitAndDedupeMessages([result.errorMessage]),
                splitAndDedupeMessages([result.warningMessage]),
                'The following error(s) were encountered attempting to advance the task:',
                'The following warning(s) were encountered attempting to advance the task:',
            );
        }
    }

    async skipReferral(): Promise<void> {
        if (!this.isBatchMode) {
            return;
        }
        const message =
            this.taskIndex < this.taskList.length - 1
                ? 'Skip this referral and move to the next one?'
                : 'You have processed or skipped all referrals. Click OK to return';
        try {
            await this._messageModalService.confirm(message, 'Confirm');
        } catch {
            return;
        }
        await this._advanceBatch();
    }

    private _turnEditModeOn() {
        this.referralForm.enable();
        this._referralPageService.updateEditMode(true);
        this._navigationService.enableNavWarning('Editing is in progress. Are you sure you want to leave?');
    }

    private _turnEditModeOff() {
        this.referralForm.disable();
        this._referralPageService.updateEditMode(false);
        this._navigationService.disableNavWarning();
    }

    private async _advanceBatch(): Promise<void> {
        if (this.taskIndex < this.taskList.length - 1) {
            this.taskIndex++;
            await this._loadReferralForTask(this.taskList[this.taskIndex]);
            this.siteDetails.setGridData();
        } else {
            this._navigateBackToActionView();
        }
    }

    private async _loadReferralForTask(taskId: number): Promise<void> {
        const busyRef = this._busyIndicatorService.show({ message: 'Loading' });
        try {
            const referral = await this._referralsService.getLitigationReferralByTaskId(taskId);
            this.referralId = referral.litigationReferralId;
        } finally {
            busyRef.hide();
        }

        await this._loadAllData();
    }

    private _navigateBackToActionView(): void {
        const route = this.actionViewStateService.getReturnRoute(this._actionViewPersistenceService.routeParams ?? {});
        this._navigationServiceHandler.go(route.state, route.params);
    }

    async launchExceptionModal() {
        const params = {
            workflowStatus: this.workflowStatus(),
            litigationReferralId: this.referralId,
        };

        const result = await this._modalService.showAsync(ReferralTaskExceptionModalComponent, params, 'modal-md');

        if (result) {
            const { succeeded, errorMessage, warningMessage } = result;
            if (succeeded) {
                // TODO ???refresh Referral Page Statusafter Exception action is successfully completed
                const busyRef = this._busyIndicatorService.show({ message: 'Refreshing Task Data' });

                try {
                    await this._setWorkflowStatus();

                    if (this.workflowStatus().isSeriesCompleted) {
                        this.referral = await this._referralsService.getLitigationReferral(
                            this.referral.litigationReferralId,
                        );
                        this._patchForm();
                    }
                } finally {
                    busyRef.hide();
                }
            } else {
                this._toastr.error(errorMessage);
            }
        }
    }

    async launchTaskModal(event: MouseEvent): Promise<void> {
        event.preventDefault();

        this._launchTaskModalService.launchTaskModal(this.referral.litigationReferralId, EntityTypeIds.LITIGATION_REFERRAL, true);
        // const taskUpdated = await this._taskService.launchTaskModal(
        //     this.referral.litigationReferralId,
        //     EntityTypeIds.LITIGATION_REFERRAL,
        //     true,
        // );

        // if (taskUpdated) {
        //     const busyRef = this._busyIndicatorService.show({ message: 'Refreshing Task Data' });

        //     try {
        //         await this._setWorkflowStatus();

        //         if (this.workflowStatus().isSeriesCompleted) {
        //             this.referral = await this._referralsService.getLitigationReferral(
        //                 this.referral.litigationReferralId,
        //             );
        //             this._patchForm();
        //         }
        //     } finally {
        //         busyRef.hide();
        //     }
        // }
    }

    async refresh() {
        await this._loadAllData('Refreshing');
        this.siteDetails.setGridData();
    }

    private async _loadAllData(message: string = 'Loading'): Promise<void> {
        const busyRef = this._busyIndicatorService.show({ message });

        try {
            const [systemReferral, originalReferral, referral] = await Promise.all([
                this._referralsService.getLitigationReferralLiveData(this.referralId),
                this._referralsService.getLitigationReferralOriginalData(this.referralId),
                this._referralsService.getLitigationReferral(this.referralId),
            ]);

            this.systemReferral = systemReferral;
            this.originalReferral = originalReferral;
            this.referral = referral;
            if (this.editMode()) {
                this._pristineReferral = structuredClone(this.referral);
            }
            await this._loadCoCounselFirmOptions(referral.coCounselFirmId);

            this._initCommentsAndAttachments();
            this._initBreadcrumbs();
            this._patchForm();
            await this._setWorkflowStatus();
            this.hasWritePermission = await this._restrictService.hasCompanyPermission(
                this.referral.companyId,
                Core.AccessRightsEnum.Write,
            );
        } finally {
            busyRef.hide();
        }
    }

    private async _loadCoCounselFirmOptions(includeCoCounselFirmId?: number): Promise<void> {
        this._coCounselFirms = await this._referralsService.getCoCounselFirms(
            includeCoCounselFirmId ? [includeCoCounselFirmId] : [],
        );
        this._setCoCounselFirmOptions();
    }

    private _setCoCounselFirmOptions(): void {
        const currentCoCounselFirmId = this.referral?.coCounselFirmId;
        this.coCounselFirmOptions = this._coCounselFirms
            .filter((firm) => firm.isActive || firm.coCounselFirmId === currentCoCounselFirmId)
            .map((firm) => ({
                name: firm.coCounselFirmName,
                value: firm.coCounselFirmId.toString(),
            }));
    }

    private _initForm(): void {
        this.referralForm = this._fb.group({
            relationshipToProperty: [''],
            engagementNumberAH: [''],
            engagementNumberJS: [''],
            clientContactName: [''],
            clientContactPhone: [''],
            clientContactEmail: [''],
            plaintiffAssessees: [''],
            coCounselFirmId: [2],
            rlsFeeTypeId: [null],
            rlsFeeRate: [null],
            feeTermsLanguage: [null],
            additionalNotes: [null],
            attyFeesPaidByClient: [null],
            rlsExpertPayorId: [null],
            rlsFilingFeePayorId: [null],
            potentialRLSFees: [null],
            potentialTaxSavings: [null],
            fixedFeeAmount: [null],
            siteName: [''],
            siteAddress1: [''],
            siteAddress2: [''],
            siteCity: [''],
            siteState: [''],
            siteZip: [''],
            siteParcelAcctNums: [''],
            propertyTypeId: [null],
            siteClassPrimary: [''],
            siteClassSecondary: [''],
            siteAssessorName: [''],
            priorAppealLevel: [''],
            priorAppealDecisionDate: [null],
            appealDeadlineDate: [null],
            appealMarketValue: [null],
            appealTargetValue: [null],
            issuesOnAppeal: [''],
            nextAppealLevel: [''],
            rlsDueDate: [null],
            haveNOD: [null],
            nodDate: [null],
            casePriorityId: [''],
            ryanContactCPName: [''],
            ryanContactEPName: [''],
            // ryanContactAHName: [''],
            ryanContactJSName: [''],
            ryanContactJSUserID: [null, Validators.required],
            // ryanContactSubmitterName: [''],
            clientName: [''],
            clientNumber: [''],
            clientAddress1: [''],
            clientAddress2: [''],
            clientCity: [''],
            clientState: [''],
            clientZip: [''],
        });
        this.referralForm.disable();
    }

    private _patchForm(): void {
        // const siteAddress = [
        //     this.referral.siteAddress1,
        //     this.referral.siteAddress2,
        //     [this.referral.siteCity, this.referral.siteState, this.referral.siteZip].filter(Boolean).join(', '),
        // ]
        //     .filter(Boolean)
        //     .join(', ');

        this.referralForm.patchValue({
            relationshipToProperty: this.referral.relationshipToProperty,
            engagementNumberAH: this.referral.engagementNumberAH,
            engagementNumberJS: this.referral.engagementNumberJS,
            clientContactName: this.referral.clientContactName,
            clientContactPhone: this.referral.clientContactPhone,
            clientContactEmail: this.referral.clientContactEmail,
            plaintiffAssessees: this.referral.plaintiffAssessees,
            coCounselFirmId: this.referral.coCounselFirmId ?? 2,
            rlsFeeTypeId: this.referral.rlsFeeTypeId,
            rlsFeeRate: this.referral.rlsFeeRate,
            feeTermsLanguage: this.referral.feeTermsLanguage,
            additionalNotes: this.referral.additionalNotes,
            attyFeesPaidByClient: this.referral.attyFeesPaidByClient,
            rlsExpertPayorId: this.referral.rlsExpertPayorId,
            rlsFilingFeePayorId: this.referral.rlsFilingFeePayorId,
            potentialRLSFees: this.referral.potentialRLSFees,
            potentialTaxSavings: this.referral.potentialTaxSavings,
            fixedFeeAmount: this.referral.fixedFeeAmount,
            siteName: this.referral.siteName,
            siteAddress1: this.referral.siteAddress1,
            siteAddress2: this.referral.siteAddress2,
            siteCity: this.referral.siteCity,
            siteState: this.referral.siteState,
            siteZip: this.referral.siteZip,
            siteParcelAcctNums: this.referral.siteParcelAcctNums,
            propertyTypeId: this.referral.propertyTypeId,
            siteClassPrimary: this.referral.siteClassPrimary,
            siteClassSecondary: this.referral.siteClassSecondary,
            siteAssessorName: this.referral.siteAssessorName,
            priorAppealLevel: this.referral.priorAppealLevel,
            priorAppealDecisionDate: this.referral.priorAppealDecisionDate,
            appealDeadlineDate: this.referral.appealDeadlineDate,
            appealMarketValue: this.referral.appealMarketValue,
            appealTargetValue: this.referral.appealTargetValue,
            issuesOnAppeal: this.referral.issuesOnAppeal,
            nextAppealLevel: this.referral.nextAppealLevel,
            rlsDueDate: this.referral.rlsDueDate,
            haveNOD: this.referral.haveNOD,
            nodDate: this.referral.nodDate,
            casePriorityId: this.referral.casePriorityId,
            ryanContactCPName: this.referral.ryanContactCPName,
            ryanContactEPName: this.referral.ryanContactEPName,
            // ryanContactAHName: this.referral.ryanContactAHName,
            ryanContactJSName: this.referral.ryanContactJSName,
            ryanContactJSUserID: this.referral.ryanContactJSUserID,
            // ryanContactSubmitterName: this.referral.ryanContactSubmitterName,
            clientName: this.referral.clientName,
            clientNumber: this.referral.clientNumber,
            clientAddress1: this.referral.clientAddress1,
            clientAddress2: this.referral.clientAddress2,
            clientCity: this.referral.clientCity,
            clientState: this.referral.clientState,
            clientZip: this.referral.clientZip,
        });
    }

    private _initCommentsAndAttachments(): void {
        this.attachmentModalData = {
            entityType: 'Litigation Referral',
            entityName: this.referral.matterId,
            disableYears: false,
            singleYear: false,
            year: this.referral.taxYear,
            entityData: {
                typeId: Core.EntityTypes.LitigationReferral,
                id: this.referral.litigationReferralId,
                name: this.referral.matterId,
            },
            readOnly: false,
            bundlerContext: this._buildBundlerContext(),
        } as AttachmentModalData;

        this.commentModalData = {
            entityID: this.referral.litigationReferralId,
            entityTypeID: Core.EntityTypes.LitigationReferral,
            entityName: this.referral.matterId,
            canEdit: true,
            description: '',
            year: this.referral.taxYear.toString(),
            disableYearSelection: true,
            disableCommentTypeSelection: true,
        } as CommentModalData;

        this.hasAttachments = this.referral.hasAttachments;
        this.hasComments = this.referral.hasComments;
    }

    /**
     * Builds the Attachment Bundler context with Litigation Referral-specific defaults so that,
     * when the user launches the bundler from the attachment modal, the entity and further-limit
     * filters are pre-applied (Company / Site(s) / Parcel(s) / Year / Appeal location / Appeal Decision type).
     */
    private _buildBundlerContext(): AttachmentBundlerContext {
        const referralAppeals = this.referral.referralAppeals || [];

        // Site IDs: distinct site IDs from referral appeals, falling back to the primary site.
        const siteIds = [...new Set(referralAppeals.map((a) => a.siteId).filter((id) => !!id))];
        if (!siteIds.length && this.referral.primarySiteId) {
            siteIds.push(this.referral.primarySiteId);
        }

        // Parcel IDs: distinct, non-null resolved parcel IDs from referral appeals.
        const parcelIds = [...new Set(referralAppeals.map((a) => a.parcelId).filter((id) => !!id))];

        // Partial-resolution warning: some appeals have a parcel account number but no resolved parcel ID.
        const unresolvedParcels = referralAppeals.filter((a) => !!a.parcelAcctNum && !a.parcelId).length;
        const parcelResolutionWarning =
            unresolvedParcels > 0
                ? `${unresolvedParcels} parcel${unresolvedParcels === 1 ? '' : 's'} on this referral could not be matched. ` +
                  'The bundler will open without preselecting those parcels.'
                : undefined;

        const alreadyAttachedIds = (this._currentAttachments || []).map((a) => a.attachmentID);

        // A company is top-level when topLevelCompanyId is absent or equals companyId.
        // This determines which backend query path the bundler uses.
        const isTopLevelCompany =
            !this.referral.topLevelCompanyId || this.referral.topLevelCompanyId === this.referral.companyId;

        return {
            bundlerSource: Core.AttachmentBundlerScopeEnum.LitigationReferral,
            companyId: this.referral.companyId,
            topLevelCompanyId: this.referral.topLevelCompanyId,
            isTopLevelCompany,
            siteIds,
            parcelIds,
            year: this.referral.taxYear,
            defaultLocations: [Core.AttachmentBundlerLocationEnum.Appeal],
            defaultCategories: [],
            defaultAttachmentTypes: [Core.AttachmentTypes.AppealDecision],
            alreadyAttachedIds,
            parcelResolutionWarning,
        } as AttachmentBundlerContext;
    }

    /**
     * Keeps the bundler context's already-attached IDs in sync as attachments change on the referral,
     * so already-attached documents remain non-selectable in the bundler.
     */
    onAllAttachmentsChanged(attachments: Attachment[]): void {
        this._currentAttachments = attachments || [];
        if (this.attachmentModalData) {
            this.attachmentModalData.bundlerContext = this._buildBundlerContext();
        }
    }

    private _initBreadcrumbs(): void {
        this.breadcrumbs = [
            {
                name: this.referral.topLevelCompanyName,
                target: 'company',
                options: { companyId: this.referral.topLevelCompanyId },
            },
        ];

        if (this.referral.topLevelCompanyId !== this.referral.companyId) {
            this.breadcrumbs.push({
                name: this.referral.companyName,
                target: 'company',
                options: { companyId: this.referral.companyId },
            });
        }

        if (this.referral.primarySiteId) {
            this.breadcrumbs.push({
                name: this.referral.siteName,
                target: 'site',
                options: { companyId: this.referral.companyId, siteId: this.referral.primarySiteId },
            });
        }
    }

    private async _setWorkflowStatus(): Promise<void> {
        const workflowStatus = await this._referralsService.getLitigationReferralWorkFlowStatus(this.referralId);
        this._referralPageService.updateWorkflowStatus(workflowStatus);
    }

    private _validatePercent(): boolean {
        const rlsFeeRateControl = this.referralForm.get('rlsFeeRate');
        console.log('rlsFeeRateControl value on save:', rlsFeeRateControl.value);
        const rlsFeeRate = this.referralForm.value.rlsFeeRate as number;

        let message;

        if (isNaN(rlsFeeRate)) {
            message = 'RLS Fee Rate must be a valid number';
        } else if (rlsFeeRate < 0) {
            message = 'RLS Fee Rate cannot be negative';
        } else if (rlsFeeRate >= 100) {
            message = 'RLS Fee Rate must be less than or equal to 99.999999';
        }

        if (message) {
            this._toastr.error(message, 'Invalid Input');
            return false;
        }

        if (rlsFeeRate !== null && rlsFeeRate !== undefined) {
            this.referralForm.patchValue({
                rlsFeeRate: Math.round(Math.min(rlsFeeRate, 99.999999) * 1e6) / 1e6, // Clamp below 100 and round to 6 decimal places
            });
        }

        return true;
    }

    private _validateForm(): boolean {
        if (this.referralForm.valid) {
            return true;
        }

        this.referralForm.markAllAsTouched();

        if (this.referralForm.get('ryanContactJSUserID')?.invalid) {
            this._toastr.error('Appeal Assigned User must have a value to save.', 'Invalid Input');
            return false;
        }

        this._toastr.error('Please correct invalid fields before saving.', 'Invalid Input');
        return false;
    }
}
